<template>
  <v-container fluid>
    <v-form @submit.prevent="submit">

      <v-card class="mb-4">
        <v-card-title class="pb-0">
          Who can access these documents?
        </v-card-title>
        <div class="pa-4 pt-0">
          <v-checkbox v-for="group in groups"
                      :key="group.id"
                      v-model="form.groups"
                      :value="group.id"
                      :label="group.name"
                      dense
                      hide-details>
          </v-checkbox>
        </div>
      </v-card>

      <v-card class="mb-4"
              v-if="form.groups.length">
        <v-card-title class="pb-0">
          What can they do with each document?
        </v-card-title>
        <div class="pa-4 pt-0">
          <v-row>
            <v-col cols="12"
                   sm="6">
              A Mobile Locker user can:
              <v-switch v-model="form.permissions.can_be_shared"
                        label="Share this file as a link via email."
                        dense
                        hide-details>
              </v-switch>
              <v-switch v-model="form.permissions.can_be_shared_link"
                        label="Create a link to this file that can be shared elsewhere."
                        dense
                        hide-details>
              </v-switch>
              <v-switch v-model="form.permissions.can_be_presented_by_user"
                        label="Present this file from the browser"
                        dense
                        hide-details>
              </v-switch>
              <v-switch v-model="form.permissions.can_be_annotated"
                        label="Annotate this file in the iOS app."
                        dense
                        hide-details>
              </v-switch>
              <v-switch v-model="form.permissions.is_rateable"
                        label="Rate this file in the iOS app."
                        dense
                        hide-details>
              </v-switch>
              <v-switch v-model="form.permissions.can_be_exported"
                        label="Export this file from the browser or desktop app."
                        dense
                        hide-details>
              </v-switch>
              <v-switch v-model="form.permissions.can_be_printed_by_user"
                        label="Print this file from the browser or desktop app."
                        dense
                        hide-details>
              </v-switch>
            </v-col>
            <v-col cols="12"
                   sm="6"
                   v-if="showViewerPermissions">
              A viewer of the shared file can:
              <v-switch v-model="form.permissions.can_be_downloaded"
                        label="Download this file."
                        dense
                        hide-details>
              </v-switch>
              <v-switch v-model="form.permissions.can_be_printed"
                        label="Print this file."
                        dense
                        hide-details>
              </v-switch>
              <v-switch v-model="form.permissions.can_be_forwarded"
                        label="Forward this file to someone else."
                        dense
                        hide-details>
              </v-switch>
            </v-col>
          </v-row>
        </div>
      </v-card>

      <v-card class="mb-4"
              v-if="showSharingCard">
        <v-card-title>
          Sharing
        </v-card-title>
        <div class="pa-4 pt-0">
          <template v-if="isQuickShareEnabled">
            <v-select v-model="form.share_template_id"
                      label="Quick Share Email Template"
                      :items="quickShareTemplates"
                      item-value="id"
                      item-text="name"
                      hint="Leave this blank to use the default quick share template."
                      persistent-hint
                      clearable>
            </v-select>

            <v-switch v-if="form.share_template_id"
                      v-model="form.is_template_required"
                      label="Require this template to be used when sharing via regular email."
                      hint="If you turn this on, a user will have to use this template to share this presentation. This is new behavior as of December, 2022."
                      persistent-hint>
            </v-switch>
          </template>

          <v-select v-model="form.share_theme_id"
                    label="Share Theme"
                    :items="shareThemes"
                    item-value="id"
                    item-text="name"
                    hint="Leave this blank to use the default."
                    persistent-hint
                    clearable
                    class="mb-4">
          </v-select>
        </div>
      </v-card>

      <v-card class="mb-4">
        <v-card-actions>
          <v-btn @click.prevent="$store.dispatch('pepperFlowCart/prevStep')">
            Go back
          </v-btn>
          <v-btn type="submit"
                 color="primary">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import pepperFlowService from '../../../services/pepperFlowService';

export default {
  data() {
    return {
      form: new Form({
        permissions: {
          ...pepperFlowService.initPermissions(),
        },
        groups: [],
        share_template_id: '',
        is_template_required: false, // MLD-3997
        share_theme_id: '',
      })
    }
  },
  computed: {
    ...mapGetters([
      'groups',
      'quickShareTemplates', // MLD-3997
      'shareTemplates',
      'shareThemes',
      'team',
      'user',
    ]),
    ...mapGetters('pepperFlowCart', [
      'cart',
    ]),
    isQuickShareEnabled() { // MLD-3997
      return this.team.sharing_enabled
        && (this.team.quickshare_app_enabled || this.team.quickshare_browser_enabled)
        && this.form.permissions
        && this.form.permissions.can_be_shared
    },
    showSharingCard() {
      return this.form.groups.length
        && this.team.sharing_enabled
        && (this.form.permissions.can_be_shared || this.form.permissions.can_be_shared_link);
    },
    showViewerPermissions() {
      return this.team.sharing_enabled
        && (this.form.permissions.can_be_shared || this.form.permissions.can_be_shared_link)
    },
  },
  created() {
    this.initForm();
  },
  methods: {
    initForm() {
      this.$store.dispatch('fetchShareTemplates');
      this.$store.dispatch('fetchShareThemes');
      this.$store.dispatch('pepperFlowCart/setPermissions', pepperFlowService.initPermissions(this.team));
      Object.keys(this.cart.permissions).forEach(key => {
        this.form.permissions[key] = this.cart.permissions[key];
      })
      this.form.groups = this.cart.groups;
      this.form.is_template_required = this.team.quickshare_template_required; // MLD-3997
    },
    submit() {
      const form = this.form.data();
      this.$store.dispatch('pepperFlowCart/setPermissions', form.permissions);
      this.$store.dispatch('pepperFlowCart/setGroups', form.groups)
      this.$store.dispatch('pepperFlowCart/setShareTemplateID', form.share_template_id);
      this.$store.dispatch('pepperFlowCart/setIsTemplateRequired', form.is_template_required); // MLD-3997
      this.$store.dispatch('pepperFlowCart/setShareThemeID', form.share_theme_id);
      this.$store.dispatch('pepperFlowCart/nextStep');
    }
  }
}
</script>
